import { GearIcon } from '@radix-ui/react-icons';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';

import { useFeatureFlags } from '@src/hooks/useFeatureFlags';

import { AccountOptions } from './AccountOptions';
import { IconButton } from './IconButton';
import { MultiSectionNav } from './MultiSectionNav';
import { SingleSectionNav } from './SingleSectionNav';
import { SiteLogo } from './SiteLogo';
import { NavigationSection } from './types';

import styles from './desktopNavigation.css';

export interface DesktopNavigationProps {
  /** The configuration for the displayed sections and links. */
  sections: readonly NavigationSection[];
  /** The logged-in user's display name or email address. */
  username: string;
  /** The path that clicking on the Ocula logo links to. */
  homePath: string;
  /** Callback for when the user clicks the logout button. */
  onLogout: () => void;
  /** Callback for when the user clicks the settings button. */
  onSettings: () => void;
}

export function DesktopNavigation({ sections, username, homePath, onLogout, onSettings }: DesktopNavigationProps) {
  const {
    values: { showFeatureFlagSettings },
  } = useFeatureFlags();
  const isSingleSection = sections.length < 2;
  return (
    <nav className={styles.navigationContainer} aria-label="Site navigation">
      <div className={styles.navigationContent}>
        <NavigationMenu.Root className={styles.navigationMenuRoot}>
          <SiteLogo homePath={homePath} compact />
          <NavigationMenu.List className={styles.navigationMenuList}>
            {isSingleSection ? (
              <SingleSectionNav links={sections?.[0]?.links} />
            ) : (
              <MultiSectionNav sections={sections} />
            )}

            <NavigationMenu.Indicator className={styles.navigationMenuIndicator}>
              <div className={styles.arrow} />
            </NavigationMenu.Indicator>
          </NavigationMenu.List>

          <div className={styles.viewportPosition}>
            <NavigationMenu.Viewport className={styles.navigationMenuViewport} />
          </div>
        </NavigationMenu.Root>

        <div className={styles.navigationControls}>
          {showFeatureFlagSettings && <IconButton onClick={onSettings} label="Settings" icon={<GearIcon />} />}
          <AccountOptions username={username} compact={false} onLogout={onLogout} />
        </div>
      </div>
    </nav>
  );
}
